import { Article } from '../articlesData';

export const articleDigitalAnsaettelseskontraktMitid: Article = {
  id: 'digital-ansaettelseskontrakt-mitid-guide',
  slug: 'digital-ansaettelseskontrakt-mitid-guide',
  title: 'Digital ansættelseskontrakt: Juridiske krav, MitID-underskrift og gratis skabelon',
  subtitle: 'Lær reglerne for digitale ansættelsesbeviser, de nye krav fra ansættelsesbevisdirektivet og hvordan du sender kontrakter til MitID-signatur på 2 minutter.',
  metaTitle: 'Digital Ansættelseskontrakt med MitID: Guide & Skabelon | KvikSign',
  metaDescription: 'Komplet guide til lovlige digitale ansættelseskontrakter i Danmark. Overhold ansættelsesbevisloven og få medarbejderens MitID-underskrift lynhurtigt.',
  canonicalUrl: 'https://kviksign.com/da/articles/digital-ansaettelseskontrakt-mitid-guide',
  heroImage: 'https://firebasestorage.googleapis.com/v0/b/kviksign-db-europe/o/Website%2FPhotos%2FHR%20and%20people.webp?alt=media&token=8e5db8f7-f4fc-4ee9-8e61-4a66b993f2e7',
  heroImageAlt: 'Digital ansættelseskontrakt underskrevet med MitID på bærbar computer',
  category: 'HR & Ansættelse',
  author: {
    name: 'KvikSign',
    role: 'eID & Sikkerhedsteam',
    avatar: '/icon.svg'
  },
  publishedAt: '2026-10-14',
  updatedAt: '2026-10-14',
  readingTime: '7 min læsetid',
  keywords: [
    'digital ansættelseskontrakt',
    'ansættelseskontrakt mitid',
    'ansættelsesbevisloven digital signatur',
    'funktionærkontrakt skabelon',
    'underskriv ansættelseskontrakt online',
    'hr digital signering',
    'mitid underskrift nyansat',
    'elektronisk ansættelsesaftale'
  ],
  keyTakeaways: [
    'Ifølge den opdaterede ansættelsesbevislov skal arbejdsgivere udlevere væsentlige arbejdsvilkår inden for 7 kalenderdage efter arbejdets påbegyndelse.',
    'Digitale ansættelseskontrakter er 100 % ligestillede med fysiske papirkontrakter, forudsat at medarbejderen har adgang til at gemme og printe dokumentet.',
    'MitID-signering sikrer, at ingen efterfølgende kan bestride underskriftens ægthed, dato eller det præcise ordlydsindhold.',
    'Ved at digitalisere ansættelsesforløbet reduceres den gennemsnitlige svartid fra 6-8 dage til under 4 timer, hvilket minimerer risikoen for, at kandidaten modtager andre jobtilbud.',
    'KvikSign sikrer GDPR-overholdelse, idet følsomme oplysninger som CPR-nummer krypteres og beskyttes mod uautoriseret adgang under forsendelse og signering.'
  ],
  tableOfContents: [
    { id: 'hvad-siger-ansaettelsesbevisloven-om-digitale-kontrakter', title: 'Hvad siger ansættelsesbevisloven om digitale kontrakter?' },
    { id: 'de-15-obligatoriske-punkter-i-en-ansaettelseskontrakt', title: 'De 15 obligatoriske oplysninger i kontrakten' },
    { id: 'hvorfor-vaelge-mitid-fremfor-almindelig-e-mail-signatur', title: 'Hvorfor vælge MitID fremfor almindelig e-mail-signatur?' },
    { id: 'gdpr-og-sikker-haandtering-af-cpr-numre', title: 'GDPR og sikker håndtering af CPR-numre' },
    { id: 'trin-for-trin-send-en-ansaettelseskontrakt-med-kviksign', title: 'Trin-for-trin: Send en ansættelseskontrakt med KvikSign' },
    { id: 'download-gratis-ansaettelseskontrakt-skabelon', title: 'Download gratis ansættelseskontrakt skabelon' },
    { id: 'faq-digital-ansaettelseskontrakt', title: 'Ofte stillede spørgsmål' }
  ],
  sections: [
    {
      id: 'hvad-siger-ansaettelsesbevisloven-om-digitale-kontrakter',
      title: 'Hvad siger ansættelsesbevisloven om digitale kontrakter?',
      paragraphs: [
        'Med ikrafttrædelsen af det opdaterede arbejdsvilkårsdirektiv i Danmark er kravene til arbejdsgiverens oplysningspligt blevet markant skærpet. Loven tillader udtrykkeligt elektronisk udlevering og underskrift af ansættelseskontrakter, men stiller tre klare betingelser:',
        '1. Medarbejderen skal have elektronisk adgang til dokumentet.',
        '2. Oplysningerne skal kunne gemmes og udskrives af medarbejderen.',
        '3. Arbejdsgiveren skal opbevare bevis for, at dokumentet er fremsendt og modtaget.',
        'En digital signering med MitID gennem KvikSign opfylder samtlige disse krav automatisk, da der udstedes en forseglet PDF med komplet revisionslog, som begge parter kan arkivere.'
      ]
    },
    {
      id: 'de-15-obligatoriske-punkter-i-en-ansaettelseskontrakt',
      title: 'De 15 obligatoriske oplysninger i kontrakten',
      paragraphs: [
        'Ansættelseskontrakten skal indeholde en række minimumsoplysninger for at være lovlig. De væsentligste omfatter:',
        '• Arbejdsgiverens og medarbejderens navn og adresse.',
        '• Arbejdsstedets beliggenhed.',
        '• Beskrivelse af arbejdet eller stillingsbetegnelse.',
        '• Ansættelsesforholdets begyndelsestidspunkt og forventet varighed ved tidsbegrænset ansættelse.',
        '• Medarbejderens rettigheder med hensyn til betalt ferie.',
        '• Opsigelsesvarsler for både medarbejder og arbejdsgiver.',
        '• Gældende løn, tillæg samt udbetalingsterminer.',
        '• Den normale daglige eller ugentlige arbejdstid samt eventuelle overarbejdsordninger.',
        '• Eventuelle kollektive overenskomster, der regulerer arbejdsforholdet.'
      ]
    },
    {
      id: 'hvorfor-vaelge-mitid-fremfor-almindelig-e-mail-signatur',
      title: 'Hvorfor vælge MitID fremfor almindelig e-mail-signatur?',
      paragraphs: [
        'Mange internationale platforme tillader underskrifter blot ved at modtageren indtaster sit navn eller tegner en streg med musen. Ved en ansættelseskontrakt er dette forbundet med betydelig juridisk risiko:',
        'Hvis der senere opstår en arbejdskonflikt (f.eks. om klausuler, løn eller opsigelsesvarsel), kan medarbejderen hævde, at en anden person i husstanden klikkede på linket, eller at e-mailen blev kompromitteret.',
        'Ved brug af MitID knyttes signaturen kryptografisk til medarbejderens CPR-validerede identitet. Dette giver status af Avanceret Elektronisk Signatur (AES), som ifølge dansk retsplejelov har uomtvistelig bevisvægt i retten.'
      ]
    },
    {
      id: 'gdpr-og-sikker-haandtering-af-cpr-numre',
      title: 'GDPR og sikker håndtering af CPR-numre',
      paragraphs: [
        'Ansættelseskontrakter indeholder altid medarbejderens CPR-nummer og lønoplysninger. Datatilsynet har gentagne gange fastslået, at fremsendelse af CPR-numre via almindelig ukrypteret e-mail er i strid med GDPR og kan udløse bødestraf.',
        'Når du sender en ansættelseskontrakt via KvikSign, overføres dokumentet via TLS 1.3 end-to-end kryptering. Modtageren skal identificere sig sikkert, før dokumentets indhold overhovedet kan åbnes, hvilket garanterer 100 % overholdelse af Datatilsynets retningslinjer.'
      ]
    },
    {
      id: 'trin-for-trin-send-en-ansaettelseskontrakt-med-kviksign',
      title: 'Trin-for-trin: Send en ansættelseskontrakt med KvikSign',
      paragraphs: [
        '1. Upload din kontrakt: Træk din færdige kontrakt som PDF ind i KvikSign.',
        '2. Angiv modtager: Indtast den nyansattes navn og e-mailadresse (eller mobilnummer til SMS-notifikation).',
        '3. Placér signaturfelter: Træk signaturfeltet ned på dokumentets sidste side.',
        '4. Send og følg status: Modtageren får en besked med det samme og underskriver med sit MitID på få sekunder.',
        '5. Automatisk arkivering: Begge parter modtager den godkendte og certifikat-forseglede PDF, komplet med tidsstempel.'
      ]
    },
    {
      id: 'download-gratis-ansaettelseskontrakt-skabelon',
      title: 'Download gratis ansættelseskontrakt skabelon',
      paragraphs: [
        'Vi har udarbejdet en juridisk kvalitetssikret skabelon til funktionæransættelse, som indeholder alle de lovpligtige vilkår i henhold til ansættelsesbevisloven.',
        'Du kan gratis hente skabelonen, tilpasse den med jeres virksomhedsoplysninger og sende den til digital signering direkte i KvikSign.'
      ]
    }
  ],
  faqs: [
    {
      question: 'Er en digital ansættelseskontrakt lige så gyldig som en underskrevet papirkontrakt?',
      answer: 'Ja, ifølge dansk aftaleret og ansættelsesbevisloven er en digital kontrakt underskrevet med MitID fuldstændig ligestillet med en fysisk underskrift.'
    },
    {
      question: 'Hvor hurtigt skal en nyansat have sin ansættelseskontrakt?',
      answer: 'Arbejdsgiveren skal oplyse om de væsentligste vilkår senest 7 kalenderdage efter, at ansættelsesforholdet er påbegyndt, og øvrige vilkår senest inden for 1 måned.'
    },
    {
      question: 'Koster det ekstra at sende ansættelseskontrakter til MitID hos KvikSign?',
      answer: 'Nej, alle MitID-transaktioner er inkluderet i KvikSigns enkle priser uden skjulte broker-gebyrer.'
    }
  ],
  sources: [
    { title: 'Beskæftigelsesministeriet - Ny ansættelsesbevislov', url: 'https://bm.dk' },
    { title: 'Datatilsynet - Retningslinjer for CPR-numre og kryptering', url: 'https://www.datatilsynet.dk' }
  ],
  disclaimer: 'Oplysningerne i denne artikel er generel vejledning og kan ikke erstatte juridisk eller fagretlig rådgivning. Ved overenskomstdækkede ansættelser skal overenskomstens særlige regler altid iagttages.'
};
